import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';
import type { ClarifyingQuestion, QuestionAnswer } from '@revelio/core';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { LLMTabContentForApi } from '../CustomDash/components/AIAssistant/convert-tab-content-to-llm';
import {
  applyLlmTabContentToTabDispatch,
  type LLMTabContentForDispatch,
  PRIMARY_FILTER_BY_AI_ENTITY,
} from '../CustomDash/components/AIAssistant/llm-tab-content-dispatch';
import type { TabContentAction } from '../CustomDash/components/PageConfigProvider/page-reducer';
import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import type { GlobalAssistantConversationTurn } from './global-ai-assistant-api';

export type GlobalAssistantConversationPayload = {
  turns: GlobalAssistantConversationTurn[];
  summary?: string;
};

const normalizePreviousPrompts = (
  previousPrompts: string[] | undefined
): string[] =>
  (previousPrompts ?? [])
    .map((prompt) => prompt.trim())
    .filter(Boolean)
    .slice(-3);

export type PageDecisionDigest = {
  title: string;
  action: 'dropped' | 'swapped' | 'filter_added';
  from?: string;
  to?: string;
  reason: string;
};

export type DashboardPageStreamEvent =
  | { type: 'thinking'; content: string }
  | { type: 'status'; content: string }
  | { type: 'intent'; content: unknown }
  | { type: 'config'; content: LLMTabContentForDispatch }
  | { type: 'filters'; content: TalentDiscoveryAiFilterSearchResponseV2 }
  | { type: 'error'; content: string }
  | { type: 'verifying'; content: string }
  | { type: 'finding'; content: PageDecisionDigest }
  | {
      type: 'questions';
      content: { questions: ClarifyingQuestion[]; triggerReason: string };
    };

export interface DashboardPageStreamResult {
  /**
   * The `PositionedItem`s from the LAST applied `config` event — i.e. the
   * items currently mounted in the tab (each dispatch replaces the previous).
   * These carry the exact ids the widgets mount with, so the narration
   * collector must be armed with these ids rather than a re-conversion of the
   * raw config (which would mint fresh, non-matching uuids — FE-2117).
   */
  dispatchedItems: PositionedItem[];
}

export async function consumeSse(
  response: Response,
  onLine: (line: string) => void,
  signal: AbortSignal | undefined
): Promise<void> {
  if (!response.body) {
    throw new Error('No response body');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal?.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        // Flush any pending multibyte bytes + a complete final line left in the
        // buffer. A stream that ends WITHOUT a trailing newline (e.g. a server
        // crash / proxy cutoff mid-event) would otherwise silently drop its
        // last line — which the turn loop reads as a clean, successful end
        // rather than the truncation it is (FE-2117).
        buffer += decoder.decode();
        if (buffer.length > 0) {
          onLine(buffer);
          buffer = '';
        }
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        onLine(line);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export async function runDashboardPageStreamCreate(params: {
  prompt: string;
  entityType: 'company' | 'geography' | 'role';
  model?: string;
  answers?: QuestionAnswer[];
  rolesTax?: string;
  skillsTax?: number;
  conversation?: GlobalAssistantConversationPayload;
  /**
   * Forwarded to the page-stream backend. When false the build best-guesses
   * instead of asking clarifying questions — set by committed builds so the
   * generation never bails to a question after the user chose to build.
   */
  allowClarification?: boolean;
  dispatch: (action: TabContentAction) => void;
  applyAiSearchFilters: (
    response: TalentDiscoveryAiFilterSearchResponseV2,
    options?: {
      primarySelectionMergeMode?: 'merge' | 'replace';
      skipTalentDiscoveryAdaptFallback?: boolean;
    }
  ) => Promise<unknown>;
  onEvent?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<DashboardPageStreamResult> {
  const {
    prompt,
    entityType,
    model,
    answers,
    rolesTax,
    skillsTax,
    conversation,
    allowClarification,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  } = params;

  const csrfToken = getAuthDashMetaCsrfToken();
  const taxonomyPayload = {
    ...(rolesTax && { rolesTax }),
    ...(skillsTax !== undefined && skillsTax !== 0 && { skillsTax }),
  };

  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/page-stream`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify({
        prompt,
        entityType,
        ...(model && { model }),
        ...(answers && answers.length > 0 && { answers }),
        ...taxonomyPayload,
        ...(conversation &&
        (conversation.turns.length > 0 || conversation.summary)
          ? { conversation }
          : {}),
        ...(allowClarification !== undefined && { allowClarification }),
      }),
      signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}`);
  }

  const primaryFilterId = PRIMARY_FILTER_BY_AI_ENTITY[entityType];

  const pendingFiltersRef: {
    current: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  } = { current: undefined };
  // Track the items from the LAST applied config event so the bridge can arm
  // the narration collector with the exact ids the widgets mounted with.
  let dispatchedItems: PositionedItem[] = [];

  await consumeSse(
    response,
    (line) => {
      if (!line.startsWith('data: ')) {
        return;
      }
      const jsonStr = line.slice(6);
      if (!jsonStr.trim()) {
        return;
      }
      try {
        const event = JSON.parse(jsonStr) as DashboardPageStreamEvent;
        onEvent?.(event);
        if (event.type === 'config') {
          const applied = applyLlmTabContentToTabDispatch(
            dispatch,
            event.content,
            primaryFilterId
          );
          dispatchedItems = applied.items;
        }
        if (event.type === 'filters') {
          pendingFiltersRef.current = event.content;
        }
      } catch {
        // ignore malformed chunks
      }
    },
    signal
  );

  const pendingFilters = pendingFiltersRef.current;
  if (pendingFilters === undefined) {
    return { dispatchedItems };
  }

  await new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, 0);
  });

  await applyAiSearchFilters(pendingFilters, {
    primarySelectionMergeMode: 'replace',
    skipTalentDiscoveryAdaptFallback: true,
  });

  return { dispatchedItems };
}

/** Streamed update of the current tab from natural language (Custom Dash). */
export async function runDashboardPageStreamUpdate(params: {
  newPrompt: string;
  entityType: 'company' | 'geography' | 'role';
  currentConfig: LLMTabContentForApi;
  currentFilterContext?: string;
  previousPrompts?: string[];
  model?: string;
  answers?: QuestionAnswer[];
  rolesTax?: string;
  skillsTax?: number;
  conversation?: GlobalAssistantConversationPayload;
  dispatch: (action: TabContentAction) => void;
  applyAiSearchFilters: (
    response: TalentDiscoveryAiFilterSearchResponseV2,
    options?: {
      primarySelectionMergeMode?: 'merge' | 'replace';
      skipTalentDiscoveryAdaptFallback?: boolean;
    }
  ) => Promise<unknown>;
  onEvent?: (event: DashboardPageStreamEvent) => void;
  signal?: AbortSignal;
}): Promise<DashboardPageStreamResult> {
  const {
    newPrompt,
    entityType,
    currentConfig,
    currentFilterContext,
    previousPrompts,
    model,
    answers,
    rolesTax,
    skillsTax,
    conversation,
    dispatch,
    applyAiSearchFilters,
    onEvent,
    signal,
  } = params;

  const csrfToken = getAuthDashMetaCsrfToken();
  const taxonomyPayload = {
    ...(rolesTax && { rolesTax }),
    ...(skillsTax !== undefined && skillsTax !== 0 && { skillsTax }),
  };
  const normalizedPreviousPrompts = normalizePreviousPrompts(previousPrompts);

  const response = await fetch(
    `${DASH_META_ROOT}/api/build-a-dash/ai-gen/page-update-from-config-stream`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-surf-token-dash-meta': csrfToken ?? '',
      },
      body: JSON.stringify({
        newPrompt,
        currentConfig,
        ...(currentFilterContext && { currentFilterContext }),
        ...(normalizedPreviousPrompts.length > 0
          ? { previousPrompts: normalizedPreviousPrompts }
          : {}),
        entityType,
        ...(model && { model }),
        ...(answers && answers.length > 0 && { answers }),
        ...taxonomyPayload,
        ...(conversation &&
        (conversation.turns.length > 0 || conversation.summary)
          ? { conversation }
          : {}),
      }),
      signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}`);
  }

  const primaryFilterId = PRIMARY_FILTER_BY_AI_ENTITY[entityType];

  const pendingFiltersRef: {
    current: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  } = { current: undefined };
  // Track the items from the LAST applied config event so the bridge can arm
  // the narration collector with the exact ids the widgets mounted with.
  let dispatchedItems: PositionedItem[] = [];

  await consumeSse(
    response,
    (line) => {
      if (!line.startsWith('data: ')) {
        return;
      }
      const jsonStr = line.slice(6);
      if (!jsonStr.trim()) {
        return;
      }
      try {
        const event = JSON.parse(jsonStr) as DashboardPageStreamEvent;
        onEvent?.(event);
        if (event.type === 'config') {
          const applied = applyLlmTabContentToTabDispatch(
            dispatch,
            event.content,
            primaryFilterId
          );
          dispatchedItems = applied.items;
        }
        if (event.type === 'filters') {
          pendingFiltersRef.current = event.content;
        }
      } catch {
        // ignore malformed chunks
      }
    },
    signal
  );

  const pendingFilters = pendingFiltersRef.current;
  if (pendingFilters === undefined) {
    return { dispatchedItems };
  }

  await new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, 0);
  });

  await applyAiSearchFilters(pendingFilters, {
    primarySelectionMergeMode: 'replace',
    skipTalentDiscoveryAdaptFallback: true,
  });

  return { dispatchedItems };
}
